import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  VStack, Button, ButtonText, Card, Heading, Text, HStack, Icon,
} from '@gluestack-ui/themed';
import { CircleCheckBig } from 'lucide-react-native';
import HtmlText from '../../../../../common/components/HtmlText';
import TaskObjectives from '../../TaskObjectives';
import type { Task } from '../../../../../common/api/models/task';

/**
 * Renders a preview card for an essay task, including title, description,
 * an "Open task" button, and (when available) task objectives.
 */
type EssayTaskPreviewProps = {
  task?: Task | null;
  loading?: boolean;
  handleOpenTask: () => void;
  module?: unknown;
} & React.ComponentProps<typeof Card>;

const EssayTaskPreview: React.FC<EssayTaskPreviewProps> = ({
  task, loading, handleOpenTask, module, ...props
}) => {
  const { t } = useTranslation();

  if (!task) return null;

  const done = task?.submission?.state === 'marked_as_done';
  const immutableDone = done && task?.evaluation?.completionPolicyEffective === 'gated';

  let buttonLabel = 'open-task';
  if (done && !immutableDone) {
    buttonLabel = 'reopen-task';
  } else if (immutableDone) {
    buttonLabel = 'view-task';
  }

  return (
    <VStack space="md">
      <Card {...props} testID="essay-task-card">
        <VStack space="md">
          <Heading size="lg">{task.title}</Heading>
          <HtmlText content={task.description} fontSize={13} />

          {/* immutable done message */}
          {immutableDone && (
            <HStack bg="$backgroundLight100" borderRadius="$md" py="$1" justifyContent="center" alignItems="center">
              <Icon as={CircleCheckBig} size="2xs" color="$textSubtle" mr="$2" />
              <Text color="$textSubtle" fontSize={12}>
                {t(
                  'student.module.task-overview.immutableDoneMessage',
                  'This task was marked as done and cannot be reopened.',
                )}
              </Text>
            </HStack>
          )}

          {/* open task button */}
          <Button size="sm" onPress={handleOpenTask} disabled={loading}>
            <ButtonText>{t(`student.module.task-overview.button-opentask.${buttonLabel}`, 'Open Task')}</ButtonText>
          </Button>
        </VStack>
      </Card>
      <TaskObjectives objectives={task?.analysis?.dimensions} />
    </VStack>
  );
};

export default EssayTaskPreview;
